import { db } from '../../db/indexeddb'
import {
  getCloudGoals,
  getCloudGoalIds,
  getCloudGoalsByIds,
  getDeletedCloudGoals,
  createCloudGoal,
  updateCloudGoal,
  deleteCloudGoal,
} from '../../supabase/queries'
import { CloudGoalSchema, CloudGoalPayloadSchema } from '../sync-schemas'
import type { ValidatedCloudGoalPayload } from '../sync-schemas'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { Goal } from '../../db/models'

/* ── Goal ─────────────────────────────────────────── */

// A Goal is a standalone user-owned entity (no project/section dependency),
// so its sync wiring mirrors Tag — the simplest cloud entity.

export const goalPullDescriptor: PullDescriptor<any> = {
  entityName: 'goal',
  settingsKey: 'lastGoalSyncTime',

  fetchRemote: (userId, since) => getCloudGoals(userId, since),
  fetchRemoteIds: (userId) => getCloudGoalIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudGoalsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudGoals(userId, since),

  safeParse: (raw) => CloudGoalSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.goals,
  getAllLocal: () => db.goals.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.goals.filter(g => !g.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => ({
    id: crypto.randomUUID(),
    title: remote.title,
    description: remote.description ?? null,
    lifeSpace: remote.life_space,
    userId: remote.user_id,
    cloudId: remote.id,
    deletedAt: null,
    importedFrom: remote.imported_from ?? null,
    createdAt: new Date(remote.created_at),
    updatedAt: new Date(remote.updated_at),
    lastModified: new Date(remote.updated_at),
    synced: true,
    syncedAt: new Date(),
  }),

  buildLocalUpdate: async (remote) => ({
    title: remote.title,
    description: remote.description ?? null,
    lifeSpace: remote.life_space,
    ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
    updatedAt: new Date(remote.updated_at),
  }),
}

/**
 * Die Herkunft (`testdata:…`, #269) nur, wenn es eine gibt. Die Spalte kam erst mit
 * `20261006120000_goals_habits_imported_from.sql`: Eine Cloud ohne sie lehnte sonst jeden Push ab, auch den
 * echter Einträge, und nach `MAX_RETRIES` Fehlschlägen wäre die Änderung verloren. Geleert wird sie nie.
 */
const importedFromField = (goal: Pick<Goal, 'importedFrom'>) => (goal.importedFrom ? { imported_from: goal.importedFrom } : {})

export const goalPushDescriptor: PushDescriptor<Goal> = {
  entityName: 'CloudGoal',
  getLocal: (id) => db.getGoal(id),
  getLocalCloudId: (g) => g.cloudId,
  getLocalId: (g) => g.id,
  table: db.goals,

  buildCreatePayload: async (goal) => CloudGoalPayloadSchema.parse({
    title: goal.title,
    description: goal.description,
    life_space: goal.lifeSpace,
    local_id: goal.id,
    ...importedFromField(goal),
  }),

  buildUpdatePayload: async (goal) => {
    const payload: ValidatedCloudGoalPayload & { deleted_at?: null } = CloudGoalPayloadSchema.parse({
      title: goal.title,
      description: goal.description,
      life_space: goal.lifeSpace,
      local_id: goal.id,
      ...importedFromField(goal),
    })
    if (!goal.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudGoal(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudGoal(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudGoal(cloudId, userId),
}
